import * as React from 'react';
import './OverviewComponent.css';

import {t} from 'react-multi-lang';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import {IOverviewProps} from "../../containers/OverviewContainer";
import ISource from "../../interfaces/ISource";
import IUsability from "../../interfaces/IUsability";

import CheckboxComponent from "../CheckboxComponent/CheckboxComponent";

interface IOverviewComponentState {
    activeRowCount: number,
}

class OverviewComponent extends React.PureComponent<IOverviewProps, IOverviewComponentState> {
    private static anyFilter(filter: any, row: any, column: any) {
        const id = filter.pivotId || filter.id;
        return row[id] !== undefined ? String(row[id]).toLowerCase().includes(filter.value.toLowerCase()) : true
    }

    private static transformData(sources: ISource[]): any[] {
        return sources.map((source: ISource) => {
            let keywordString = '';

            if (source.keywords !== undefined) {
                for (const keyword of source.keywords) {
                    keywordString += keywordString === '' ? `${keyword}` : `, ${keyword}`;
                }
            }

            let authorString = '';

            for (const author of source.authors) {
                authorString += authorString === '' ? `${author}` : `; ${author}`;
            }
            return {
                ...source,
                abstract: source.abstract !== '' ? 'true' : 'false',
                authors: authorString,
                keywords: keywordString,
                usability: IUsability[source.usability]
            }
        })
    }

    private static getTrProps(state: any, rowInfo: any) {
        if (typeof rowInfo !== "undefined" && rowInfo.original) {
            switch (rowInfo.original.usability) {
                case IUsability[IUsability.Rejected]:
                    return {
                        style: {
                            background: '#ed0036',
                            color: 'white',
                        }
                    };
                case IUsability[IUsability.Accepted]:
                    return {
                        style: {
                            background: '#00b146',
                            color: 'white',
                        }
                    };
                case IUsability[IUsability.Tentative]:
                    return {
                        style: {
                            background: '#00a6bb',
                            color: 'white',
                        }
                    };
                default:
                    return {
                        style: {}
                    };
            }
        } else {
            return {
                style: {
                    background: 'white',
                    color: 'black'
                },
            }
        }
    }

    private selectTable: any;

    constructor(props: IOverviewProps) {
        super(props);

        this.state = {
            activeRowCount: props.sources.length,
        }

        this.getColumns = this.getColumns.bind(this);
        this.emptyClickAction = this.emptyClickAction.bind(this);
        this.prepareClickAction = this.prepareClickAction.bind(this);
        this.isSelected = this.isSelected.bind(this);
        this.isSelectable = this.isSelectable.bind(this);
        this.isDeselectable = this.isDeselectable.bind(this);
        this.isDeletable = this.isDeletable.bind(this);
        this.selectAll = this.selectAll.bind(this);
        this.deselectAll = this.deselectAll.bind(this);
        this.handleTableChange = this.handleTableChange.bind(this);
    }

    public componentDidUpdate() {
        this.handleTableChange();
    }

    public render() {
        const header =
            <div className="w_header">{t('overview.overview')}</div>;

        const table =
            <React.Fragment>
                <div className="w_overview_control">
                    <button
                        className={`float-left btn btn-sm btn-success mr-1 ${this.props.sources.length === 0 && 'disabled'}`}
                        disabled={this.props.sources.length === 0}
                        onClick={this.emptyClickAction}
                        title={t('overview.buttons.eval_hover')}>
                        {t('overview.buttons.eval')}
                    </button>
                    <button
                        className={`float-right btn btn-sm btn-danger mr-1 ${!this.isDeletable() && 'disabled'}`}
                        disabled={!this.isDeletable()}
                        onClick={this.props.dispatchDeleteSelectedSources}
                        title={t('overview.buttons.delete_hover')}>
                        {t('overview.buttons.delete')}
                    </button>
                    <button
                        className={`float-right btn btn-sm btn-secondary mr-1 ${!this.isDeselectable() && 'disabled'}`}
                        disabled={!this.isDeselectable()}
                        onClick={this.deselectAll}
                        title={t('overview.buttons.deselect_hover')}>
                        {t('overview.buttons.deselect')}
                    </button>
                    <button
                        className={`float-right btn btn-sm btn-secondary mr-1 ${!this.isSelectable() && 'disabled'}`}
                        disabled={!this.isSelectable()}
                        onClick={this.selectAll}
                        title={t('overview.buttons.select_hover')}>
                        {t('overview.buttons.select', {count: this.state.activeRowCount.toString()})}
                    </button>
                </div>
                <ReactTable
                    className="w_component w_main_table -highlight"
                    data={this.props.sources}
                    resolveData={OverviewComponent.transformData}
                    columns={this.getColumns()}
                    key={this.props.sources.length}
                    showPagination={false}
                    filterable={true}
                    defaultFilterMethod={OverviewComponent.anyFilter}
                    minRows={1}
                    defaultPageSize={this.props.sources.length}
                    getTrProps={OverviewComponent.getTrProps}
                    ref={(e) => {
                        this.selectTable = e
                    }}
                    onFilteredChange={this.handleTableChange}
                />
            </React.Fragment>;
        return (
            <React.Fragment>
                {header}
                {table}
            </React.Fragment>
        );
    }

    private getColumns() {
        return [
            {
                Cell: (row: any) => (
                    <CheckboxComponent checked={this.isSelected(row.original)}
                                       onClick={() => this.props.dispatchToggleSelect(row.original._id)}/>
                ),
                Header: t('overview.table.select'),
                filterable: false,
                minWidth: 10,
                resizable: false,
                sortable: false,
            },
            {
                Header: t('overview.table.author'),
                accessor: 'authors',
                minWidth: 20,
            },
            {
                Header: t('overview.table.title'),
                accessor: 'title',
                minWidth: 30,
            },
            {
                Header: t('overview.table.keywords'),
                accessor: 'keywords',
                minWidth: 20,
            },
            {
                Header: t('overview.table.year'),
                accessor: 'year',
                minWidth: 5,
            },
            {
                Header: t('overview.table.contribution'),
                accessor: 'usability',
                minWidth: 10,
            },
            {
                Cell: (row: any) => (
                    <div className="w_horizontal_center_flex">
                        <button
                            className="w_btn_bg_white btn btn-sm btn-outline-secondary py-0"
                            onClick={this.prepareClickAction(row.original._id)}
                            title={t('overview.table.details_hover')}>
                            {t('overview.table.details')}
                        </button>
                    </div>
                ),
                Header: t('overview.table.details'),
                filterable: false,
                minWidth: 16,
                resizable: false,
                sortable: false,
            },
        ];
    }

    private emptyClickAction() {
        this.props.history.push(`/evaluate`);
    }

    private prepareClickAction(id: string) {
        return () => {
            this.props.history.push(`/evaluate?id=${id}`);
        };
    }

    private isSelected(source: ISource): boolean {
        if (source._id === undefined) {
            return false;
        }
        return this.props.selected.indexOf(source._id) !== -1;
    }

    private isSelectable(): boolean {
        return this.props.selected.length < this.state.activeRowCount;
    }

    private isDeselectable(): boolean {
        return this.props.selected.length !== 0;
    }

    private isDeletable(): boolean {
        return this.props.selected.length > 0;
    }

    private selectAll() {
        if (!this.selectTable) {
            return;
        }
        const currentIDs = this.selectTable.getResolvedState().sortedData.map((r: any) => r._original._id);
        this.props.dispatchSelectAll(currentIDs);
    }

    private deselectAll() {
        if (!this.selectTable) {
            return;
        }
        const currentIDs = this.selectTable.getResolvedState().sortedData.map((r: any) => r._original._id);
        this.props.dispatchDeselectAll(currentIDs);
    }

    private handleTableChange() {
        if (!this.selectTable) {
            return;
        }
        this.setState({
            ...this.state,
            activeRowCount: this.selectTable.getResolvedState().sortedData.length,
        })
    }
}

export default OverviewComponent;



// WEBPACK FOOTER //
// ./src/components/OverviewComponent/OverviewComponent.tsx